// SPDX-License-Identifier: LicenseRef-Techsafe-Proprietary
// Copyright (c) 2026 Techsafe Teknoloji ve Muhendislik A.S.
// https://techsafe-solutions.com
// Economic rights: Techsafe A.S.
// Authors' moral rights reserved under Turkish Copyright Law (FSEK 5846).
// Mali haklar Techsafe A.S.'ye aittir; eser sahiplerinin manevi haklari saklidir (FSEK 5846).
// This content was prepared with the assistance of AI tools, reviewed by a
// human, and published by Techsafe Teknoloji ve Muhendislik A.S. The publishing
// entity is responsible for accuracy and compliance.
// Bu icerik, yapay zeka destekli araclar kullanilarak hazirlanmis, insan
// incelemesinden gecirilmis ve Techsafe Teknoloji ve Muhendislik A.S.
// tarafindan yayimlanmistir. Icerigin dogrulugundan yayimlayan kurulus
// sorumludur.

// Bridge CONTROL-PLANE types (ADR-002 P3).
//
// These mirror, one-to-one, the picklist/field shape committed on `main` in
// src/data-models/db_service_request_bridge.ds — the ADR-002 bridge record.
// They are NOT mocks: the control plane is fixed by an Accepted ADR and an
// existing data model. Only the per-request-kind PAYLOAD shapes (data plane)
// are provisional until #13 lands (see ../contracts).

/**
 * `request_type` picklist values from db_service_request_bridge.ds.
 * Each is one request kind on ADR-002's single server-side seam ("one door").
 * "None" is the form's initial value and never issued as a live request.
 */
export type BridgeRequestType =
  | "GetDeviceBySerialNumber"
  | "GetDeviceBySoftwareId"
  | "GetAU0004LatestAccountability"
  | "GetAU0008LatestAccountability"
  | "GetFirmwarePackageMetaByReferenceId"
  | "GetAU0001LatestAttributionByDeviceAnchor"
  | "ListFirmwarePackageMeta";

/** `status` picklist values — ADR-002 P2/P5 state-driven handoff lifecycle. */
export type BridgeStatus = "Idle" | "Pending" | "Success" | "Error";

/** Terminal states the widget polls toward (ADR-002 P4/P5). */
export const TERMINAL_STATUSES: readonly BridgeStatus[] = ["Success", "Error"];

export function isTerminal(status: BridgeStatus): boolean {
  return TERMINAL_STATUSES.includes(status);
}

/**
 * One bridge record as the widget observes it while polling. `request_payload`
 * and `response_payload` are attached content on the real form (ADR-002 P3);
 * across the bridge boundary the widget works with the already-parsed JSON of
 * that content, typed per request kind in ../contracts.
 */
export interface BridgeRecord<TResponse = unknown> {
  readonly correlationId: string;
  readonly requestType: BridgeRequestType;
  status: BridgeStatus;
  /** Response data for a Success terminal state (P5). */
  response?: TResponse;
  /** Error/halt information for an Error terminal state (P5; ADR-005 D3 halt_reason). */
  firstError?: string;
}

/**
 * The client-side bridge transport contract. One implementation talks to Zoho
 * Creator (create record, poll by correlation_id); another is the in-repo mock
 * (../contracts/mock-transport) used until #15's resolver seam exists.
 *
 * `submit` creates one stage record (ADR-007 D2: one record per stage) and
 * resolves once that record reaches a terminal state, or rejects on the bounded
 * give-up (ADR-002 P4).
 */
export interface BridgeTransport {
  submit<TRequest, TResponse>(
    input: BridgeSubmitInput<TRequest>,
  ): Promise<BridgeRecord<TResponse>>;
}

export interface BridgeSubmitInput<TRequest> {
  readonly correlationId: string;
  readonly requestType: BridgeRequestType;
  readonly request: TRequest;
  /** Bounded give-up controls (ADR-002 P4). */
  readonly pollIntervalMs?: number;
  readonly timeoutMs?: number;
  readonly signal?: AbortSignal;
}
